import {
  Button,
  Input,
  Modal,
  notify,
  useConfirmModal,
} from '@affine/component';
import { SettingRow } from '@affine/component/setting-components';
import {
  PersonalApiKeyService,
  ServerService,
} from '@affine/core/modules/cloud';
import { WorkspaceService } from '@affine/core/modules/workspace';
import {
  ApiKeyAccessMode,
  type PersonalApiKeyMetadataFragment,
} from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { useEffect, useState } from 'react';

import * as styles from './api-keys-panel.css';

const date = (value: string) => new Date(value).toLocaleString();
type Revealed = { token: string; credential: PersonalApiKeyMetadataFragment };

export const ApiKeysPanel = () => {
  const service = useService(PersonalApiKeyService);
  const t = useI18n();
  const server = useService(ServerService).server;
  const workspace = useService(WorkspaceService).workspace;
  const { openConfirmModal } = useConfirmModal();
  const keys = useLiveData(service.keys$) ?? [];
  const loading = useLiveData(service.loading$);
  const error = useLiveData(service.error$);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [accessMode, setAccessMode] = useState(ApiKeyAccessMode.READ_ONLY);
  const [expirationDays, setExpirationDays] = useState(90);
  const [revealed, setRevealed] = useState<Revealed | null>(null);
  const [workspaceId, setWorkspaceId] = useState(
    workspace.flavour === 'local' ? '' : workspace.id
  );
  const base = server.baseUrl.replace(/\/$/, '');

  useEffect(() => {
    service.revalidate().catch(() => {});
  }, [service]);

  const mutate = async (action: () => Promise<void>) => {
    setBusy(true);
    try {
      await action();
    } catch {
      notify.error({ title: t['com.affine.settings.api-keys.update-error']() });
    } finally {
      setBusy(false);
    }
  };
  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      notify.success({ title: t['Copied to clipboard']() });
    } catch {
      notify.error({ title: t['com.affine.settings.api-keys.copy-error']() });
    }
  };
  const create = () =>
    mutate(async () => {
      setRevealed(
        await service.create({
          name: name.trim(),
          accessMode,
          expirationDays: expirationDays === 0 ? null : expirationDays,
        })
      );
      setCreating(false);
      setName('');
      setAccessMode(ApiKeyAccessMode.READ_ONLY);
      setExpirationDays(90);
    });
  const rotate = (key: PersonalApiKeyMetadataFragment) =>
    openConfirmModal({
      title: t['com.affine.settings.api-keys.rotate-title']({ name: key.name }),
      description: t['com.affine.settings.api-keys.rotate-description'](),
      confirmText: t['com.affine.settings.api-keys.rotate-confirm'](),
      onConfirm: () =>
        mutate(async () => {
          setRevealed(
            await service.rotate(key.id, key.expiresAt === null ? null : 90)
          );
        }),
    });
  const revoke = (key: PersonalApiKeyMetadataFragment) =>
    openConfirmModal({
      title: t['com.affine.settings.api-keys.revoke-title']({ name: key.name }),
      description: t['com.affine.settings.api-keys.revoke-description'](),
      confirmText: t['com.affine.settings.api-keys.revoke-confirm'](),
      confirmButtonOptions: { variant: 'error' },
      onConfirm: () =>
        mutate(async () => {
          await service.revoke(key.id);
        }),
    });
  const mcp = JSON.stringify(
    {
      mcpServers: {
        affine: {
          type: 'streamable-http',
          url: `${base}/api/workspaces/${encodeURIComponent(workspaceId || 'WORKSPACE_ID')}/mcp`,
          headers: {
            Authorization: `Bearer ${revealed?.token ?? 'YOUR_API_KEY'}`,
          },
        },
      },
    },
    null,
    2
  );
  const example = `curl '${base}/api/v1/workspaces' \\\n  -H "Authorization: Bearer $AFFINE_API_KEY"`;

  return (
    <SettingRow
      name={t['com.affine.settings.api-keys.name']()}
      desc={t['com.affine.settings.api-keys.description']()}
      spreadCol={false}
    >
      <div className={styles.panel}>
        <div className={styles.actions}>
          <a
            href={`${base}/api/v1/openapi.json`}
            target="_blank"
            rel="noreferrer"
          >
            {t['com.affine.settings.api-keys.openapi']()}
          </a>
          <Button
            variant="primary"
            className={styles.createButton}
            onClick={() => setCreating(true)}
          >
            {t['com.affine.settings.api-keys.create']()}
          </Button>
        </div>
        {loading ? (
          <span>{t['Loading']()}</span>
        ) : error ? (
          <div role="alert">
            {t['com.affine.settings.api-keys.load-error']()}{' '}
            <Button
              onClick={() => {
                service.revalidate().catch(() => {});
              }}
            >
              {t['Retry']()}
            </Button>
          </div>
        ) : keys.length === 0 ? (
          <p>{t['com.affine.settings.api-keys.empty']()}</p>
        ) : (
          keys.map(key => (
            <div key={key.id} className={styles.row}>
              <div className={styles.details}>
                <strong>{key.name}</strong>
                <span>
                  {key.accessMode === ApiKeyAccessMode.READ_WRITE
                    ? t['com.affine.settings.api-keys.read-write']()
                    : t['com.affine.settings.api-keys.read-only']()}{' '}
                  ·{' '}
                  {t[
                    `com.affine.integration.mcp-server.status.${key.status.toLowerCase() as 'active' | 'rotating' | 'expired' | 'revoked'}`
                  ]()}{' '}
                  · •••• {key.fingerprint}
                </span>
                <span>
                  {t['com.affine.settings.api-keys.created']({
                    date: date(key.createdAt),
                  })}{' '}
                  ·{' '}
                  {key.expiresAt
                    ? t['com.affine.settings.api-keys.expires']({
                        date: date(key.expiresAt),
                      })
                    : t['com.affine.settings.api-keys.never-expires']()}
                </span>
                <span>
                  {t['com.affine.settings.api-keys.last-used']({
                    date: key.lastUsedAt
                      ? date(key.lastUsedAt)
                      : t['com.affine.settings.api-keys.never'](),
                  })}
                </span>
                {key.graceEndsAt && key.status === 'ROTATING' ? (
                  <span>
                    {t['com.affine.settings.api-keys.grace']({
                      date: date(key.graceEndsAt),
                    })}
                  </span>
                ) : null}
              </div>
              {!['REVOKED', 'EXPIRED'].includes(key.status) ? (
                <div className={styles.actions}>
                  <Button disabled={busy} onClick={() => rotate(key)}>
                    {t['com.affine.integration.mcp-server.action.rotate']()}
                  </Button>
                  <Button
                    variant="error"
                    disabled={busy}
                    onClick={() => revoke(key)}
                  >
                    {t['com.affine.settings.api-keys.revoke']()}
                  </Button>
                </div>
              ) : null}
            </div>
          ))
        )}
        <div className={styles.details}>
          <strong>{t['com.affine.settings.api-keys.rest-title']()}</strong>
          <p>{t['com.affine.settings.api-keys.rest-help']()}</p>
          <pre className={styles.code}>{example}</pre>
          <Button onClick={() => void copy(example)}>
            {t['com.affine.settings.api-keys.copy-rest']()}
          </Button>
          <p>{t['com.affine.settings.api-keys.access-help']()}</p>
        </div>
      </div>
      <Modal
        open={creating}
        onOpenChange={open => {
          if (!busy) setCreating(open);
        }}
        contentOptions={{ className: styles.modal }}
      >
        <div className={styles.panel}>
          <h2>{t['com.affine.settings.api-keys.create']()}</h2>
          <label className={styles.details}>
            {t['com.affine.settings.api-keys.field-name']()}
            <Input
              value={name}
              onChange={setName}
              maxLength={64}
              placeholder={t['com.affine.settings.api-keys.placeholder-name']()}
              autoFocus
            />
          </label>
          <label className={styles.details}>
            {t['com.affine.settings.api-keys.field-access']()}
            <select
              value={accessMode}
              onChange={event =>
                setAccessMode(event.target.value as ApiKeyAccessMode)
              }
            >
              <option value={ApiKeyAccessMode.READ_ONLY}>
                {t['com.affine.settings.api-keys.read-only']()}
              </option>
              <option value={ApiKeyAccessMode.READ_WRITE}>
                {t['com.affine.settings.api-keys.read-write']()}
              </option>
            </select>
          </label>
          <label className={styles.details}>
            {t['com.affine.settings.api-keys.field-expiry']()}
            <select
              aria-label={t['com.affine.settings.api-keys.field-expiry']()}
              value={expirationDays}
              onChange={event => setExpirationDays(Number(event.target.value))}
            >
              {[30, 90, 365].map(days => (
                <option key={days} value={days}>
                  {t['com.affine.settings.api-keys.expiry-days']({
                    days: String(days),
                  })}
                </option>
              ))}
              <option value={0}>
                {t['com.affine.settings.api-keys.never-expires']()}
              </option>
            </select>
          </label>
          <p>{t['com.affine.settings.api-keys.scope']()}</p>
          <div className={styles.actions}>
            <Button disabled={busy} onClick={() => setCreating(false)}>
              {t['Cancel']()}
            </Button>
            <Button
              variant="primary"
              loading={busy}
              disabled={busy || !name.trim()}
              onClick={() => {
                create().catch(() => {});
              }}
            >
              {t['com.affine.settings.api-keys.create-confirm']()}
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        open={revealed !== null}
        onOpenChange={open => {
          if (!open) setRevealed(null);
        }}
        contentOptions={{ className: styles.modal }}
      >
        {revealed ? (
          <div className={styles.panel}>
            <h2>{t['com.affine.settings.api-keys.reveal-title']()}</h2>
            <p role="status">
              {t['com.affine.settings.api-keys.reveal-warning']()}
            </p>
            <strong>{revealed.credential.name}</strong>
            <pre className={styles.code}>{revealed.token}</pre>
            <Button onClick={() => void copy(revealed.token)}>
              {t['com.affine.settings.api-keys.copy-key']()}
            </Button>
            <label className={styles.details}>
              {t['com.affine.settings.api-keys.field-workspace']()}
              <Input
                value={workspaceId}
                onChange={setWorkspaceId}
                placeholder={t[
                  'com.affine.settings.api-keys.placeholder-workspace'
                ]()}
              />
            </label>
            <pre className={styles.code}>{mcp}</pre>
            <Button
              disabled={!workspaceId.trim()}
              onClick={() => void copy(mcp)}
            >
              {t['com.affine.settings.api-keys.copy-mcp']()}
            </Button>
            <Button onClick={() => setRevealed(null)}>
              {t['com.affine.integration.mcp-server.action.done']()}
            </Button>
          </div>
        ) : null}
      </Modal>
    </SettingRow>
  );
};
